import React, { useState, useMemo, useEffect } from 'react';
import { BsFileText, BsDownload } from 'react-icons/bs';
import { useCustomer } from './CustomerContext';
import StatusBadge from './components/StatusBadge';
import EmptyState from './components/EmptyState';
import Pagination from './components/Pagination';
import { formatDate, formatEur, apiFetch } from '../../utils/api';

const PER_PAGE = 20;

const OPEN_STATES = ['borrador', 'enviado', 'pendiente'];
const isExpired = (b) => {
  if (!b.valid_until) return false;
  if (!OPEN_STATES.includes(String(b.estado || '').toLowerCase())) return false;
  const d = new Date(b.valid_until);
  return !Number.isNaN(d.getTime()) && d < new Date(new Date().toDateString());
};

const CustomerBudgetsPage = () => {
  const { budgets, loading, authHeaders } = useCustomer();
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [page, setPage] = useState(1);
  const [downloadingId, setDownloadingId] = useState(null);
  const [error, setError] = useState('');

  const downloadPdf = async (bud) => {
    setDownloadingId(bud.id);
    setError('');
    try {
      const res = await apiFetch(`/api/v1/software/customer/portal/budgets/${bud.id}/pdf`, { headers: authHeaders() });
      if (!res.ok) { setError('No se pudo generar el documento del presupuesto.'); return; }
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${(bud.presupuesto || `presupuesto-${bud.id}`).replace(/[^a-zA-Z0-9._-]+/g, '-')}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      setError('No se pudo descargar el documento.');
    } finally {
      setDownloadingId(null);
    }
  };

  const filtered = useMemo(() => {
    let list = budgets;
    if (search) {
      const q = search.toLowerCase();
      list = list.filter((b) => (b.presupuesto || `#${b.id}`).toLowerCase().includes(q));
    }
    if (statusFilter) {
      list = list.filter((b) => String(b.estado || '').toLowerCase() === statusFilter.toLowerCase());
    }
    return list;
  }, [budgets, search, statusFilter]);

  useEffect(() => {
    const totalPages = Math.max(1, Math.ceil(filtered.length / PER_PAGE));
    setPage(p => Math.min(p, totalPages));
  }, [filtered.length]);

  const paged = filtered.slice((page - 1) * PER_PAGE, page * PER_PAGE);
  const statuses = [...new Set(budgets.map((b) => b.estado).filter(Boolean))];

  if (loading) {
    return (
      <div>
        <div className="cp-skeleton cp-skeleton-title" style={{ width: 220, height: 28, marginBottom: 16 }} />
        <div className="cp-skeleton cp-skeleton-card" style={{ height: 300 }} />
      </div>
    );
  }

  return (
    <div>
      <h1 className="cp-page-title">Presupuestos</h1>

      {error && <div className="cp-alert cp-alert-error" style={{ marginBottom: 12 }}>{error}</div>}

      {!budgets.length ? (
        <EmptyState icon={BsFileText} title="Sin presupuestos" subtitle="Aún no tienes presupuestos." />
      ) : (
        <>
          <div className="cp-filter-bar">
            <input
              className="cp-search-input"
              placeholder="Buscar por numero..."
              value={search}
              onChange={(e) => { setSearch(e.target.value); setPage(1); }}
            />
            <select className="cp-select" value={statusFilter} onChange={(e) => { setStatusFilter(e.target.value); setPage(1); }}>
              <option value="">Todos los estados</option>
              {statuses.map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
          </div>

          <div className="cp-card-static">
            <div style={{ overflowX: 'auto' }}>
              <table className="cp-table">
                <thead>
                  <tr>
                    <th>Presupuesto</th>
                    <th>Fecha</th>
                    <th>Validez</th>
                    <th style={{ textAlign: 'right' }}>Base</th>
                    <th style={{ textAlign: 'right' }}>IVA</th>
                    <th style={{ textAlign: 'right' }}>Total</th>
                    <th>Estado</th>
                    <th className="cp-th-pdf"></th>
                  </tr>
                </thead>
                <tbody>
                  {!paged.length ? (
                    <tr><td colSpan="8"><EmptyState icon={BsFileText} title="Sin resultados" subtitle="Prueba con otros filtros." /></td></tr>
                  ) : paged.map((b) => {
                    const expired = isExpired(b);
                    return (
                      <tr key={b.id}>
                        <td style={{ fontWeight: 500 }}>{b.presupuesto || `#${b.id}`}</td>
                        <td>{formatDate(b.fecha)}</td>
                        <td style={expired ? { color: '#b42318', fontWeight: 600 } : undefined}>
                          {b.valid_until ? formatDate(b.valid_until) : '-'}
                        </td>
                        <td style={{ textAlign: 'right' }}>{formatEur(b.base_imponible)}</td>
                        <td style={{ textAlign: 'right' }}>{formatEur(b.iva)}</td>
                        <td style={{ textAlign: 'right', fontWeight: 600 }}>{formatEur(b.total)}</td>
                        <td><StatusBadge status={expired ? 'caducado' : b.estado} /></td>
                        <td className="cp-td-pdf">
                          <button type="button" className="cp-btn-pdf" onClick={() => downloadPdf(b)} disabled={downloadingId === b.id} title="Descargar presupuesto">
                            <BsDownload /> {downloadingId === b.id ? '...' : 'PDF'}
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            <div style={{ padding: '0 16px' }}>
              <Pagination total={filtered.length} page={page} perPage={PER_PAGE} onPageChange={setPage} />
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default CustomerBudgetsPage;
